import { Node, Edge } from 'reactflow';

/**
 * Find root nodes (nodes with no incoming edges)
 */
export const findRootNodes = (nodes: Node[], edges: Edge[]): Node[] => {
  const hasIncomingEdges: Record<string, boolean> = {};
  
  // Mark nodes with incoming edges
  edges.forEach(edge => {
    hasIncomingEdges[edge.target] = true;
  });
  
  // Return nodes without incoming edges
  return nodes.filter(node => !hasIncomingEdges[node.id]);
};

/**
 * Group nodes by their root node (DAG)
 */
export const groupNodesByDAG = (nodes: Node[], edges: Edge[]): Record<string, string[]> => {
  console.log("=== GROUPING DEBUG ===");
  console.log("Nodes for grouping:", nodes.length);
  console.log("Edges for grouping:", edges.length);
  
  // Find root nodes (nodes with no incoming edges)
  const hasIncomingEdges: Record<string, boolean> = {};
  edges.forEach(edge => {
    hasIncomingEdges[edge.target] = true;
  });
  
  const rootNodeIds = nodes
    .filter(node => !hasIncomingEdges[node.id])
    .map(node => node.id);
    
  console.log("Root nodes found:", rootNodeIds.length);
  console.log("Root node IDs:", rootNodeIds);
  
  // Group nodes by their root node
  const nodeGroups: Record<string, string[]> = {};
  
  // Initialize groups with root nodes
  rootNodeIds.forEach(rootId => {
    nodeGroups[rootId] = [rootId];
  });
  
  // BFS to assign nodes to their root's group
  rootNodeIds.forEach(rootId => {
    const queue = [rootId];
    const visited = new Set([rootId]);
    
    while (queue.length > 0) {
      const current = queue.shift()!;
      
      // Find outgoing edges
      edges.forEach(edge => {
        if (edge.source === current && !visited.has(edge.target)) {
          visited.add(edge.target);
          queue.push(edge.target);
          nodeGroups[rootId].push(edge.target);
        }
      });
    }
  });
  
  console.log("Final groups:", Object.keys(nodeGroups).length);
  Object.entries(nodeGroups).forEach(([rootId, members]) => {
    console.log(`Group ${rootId}: ${members.length} members`);
  });
  
  return nodeGroups;
};

/**
 * Apply visual grouping to nodes based on their DAG
 */
export const applyGroupStyling = (
  nodes: Node[], 
  nodeGroups: Record<string, string[]>
): Node[] => {
  const groupColors = [
    'rgba(59, 130, 246, 0.1)', // blue
    'rgba(16, 185, 129, 0.1)', // green
    'rgba(249, 115, 22, 0.1)', // orange
    'rgba(139, 92, 246, 0.1)', // purple
    'rgba(236, 72, 153, 0.1)'  // pink
  ];
  
  return nodes.map(node => {
    // Find which group this node belongs to
    const groupIndex = Object.entries(nodeGroups).findIndex(
      ([_, group]) => group.includes(node.id)
    );
    
    if (groupIndex >= 0) {
      return {
        ...node,
        style: {
          ...node.style,
          backgroundColor: groupColors[groupIndex % groupColors.length]
        },
        data: {
          ...node.data,
          groupIndex
        }
      };
    }
    
    return node;
  });
};

/**
 * Get color for node status
 */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'ghost': return 'text-gray-400';
    case 'pending': return 'text-yellow-500';
    case 'hydrating': return 'text-blue-500';
    case 'fresh': return 'text-green-500';
    case 'stale': return 'text-orange-500';
    case 'locked': return 'text-red-500';
    default: return 'text-gray-400';
  }
};

/**
 * Get edge color based on status and theme
 */
export const getEdgeColor = (status: string, darkMode: boolean): string => {
  switch (status) {
    case 'ghost': return darkMode ? '#6B7280' : '#9CA3AF';
    case 'pending': return darkMode ? '#F59E0B' : '#FBBF24';
    case 'hydrating': return darkMode ? '#3B82F6' : '#60A5FA';
    case 'fresh': return darkMode ? '#10B981' : '#34D399';
    case 'stale': return darkMode ? '#F97316' : '#FB923C';
    case 'locked': return darkMode ? '#EF4444' : '#F87171';
    default: return darkMode ? '#6B7280' : '#9CA3AF';
  }
};

/**
 * Find all dependencies of a node (recursively)
 */
export const findNodeDependencies = (
  nodeId: string, 
  edges: Edge[]
): Set<string> => {
  const dependencies = new Set<string>();
  
  const findDependencies = (id: string) => {
    edges.forEach(edge => {
      if (edge.source === id) {
        dependencies.add(edge.target);
        findDependencies(edge.target);
      }
    });
  };
  
  findDependencies(nodeId);
  return dependencies;
};
